import { Mixins, Component, Prop } from 'vue-property-decorator'
import MixinInterface from '@/_mixins/index'

import DialogTemplate from '@/_components/dialog/template/template.vue'
import UserAvatar from '@/_components/avatar/user/user.vue'

import ProjectUser from '@/views/Authorization/models/projectUser'
import UserDeletePromptDialog from '@/views/Authorization/dialogs/userDeletePrompt.vue'
import ProjectContractorSelect from '@/views/Authorization/components/projectContractorSelect.vue'
import PhoneInput from '@/_components/input/phone/phone.vue'
import JsonApiObject from '@/_models/JsonApi'
import { getRequest, postRequest } from '@/plugins/api-axios'
import { deserialise, serialise } from 'kitsu-core'
import { getURL } from '@/utils/axios'
import { BklrAccess } from '@/views/Authorization/types/projectUser'

type UserInfo = {
    cardId: Maybe<string>
    connectedVisitorCardId: Maybe<string>
    bklrAccess: Maybe<BklrAccess>
}

@Component({
    inheritAttrs: false,
    name: 'UserEdit',
    components: {
        DialogTemplate,
        UserAvatar,
        PhoneInput,
        ProjectContractorSelect,
    },
})
export default class UserEdit extends Mixins<MixinInterface>() {
    @Prop({ required: true }) props!: any

    valid = true
    loading = false
    jobTitle = this.user['jobTitle']
    jobDescription = this.user['jobDescription']
    preconditionFailed = false
    error = false
    contractor = this.user.displayContractor || null
    userInfo: Maybe<UserInfo> = null
    virtualCardUserFormValid = false
    virtualCardUser = {
        firstName: '',
        lastName: '',
        mobile: '',
        birthDate: '',
    }
    useVirtualCard = false
    dateOfBirthMenu = false
    connectedVisitorCardId = null as Maybe<string>
    connectingVisitorCard = false
    removingVisitorCard = false
    loadingVirtualCard = false

    hasBklrAccess = false
    bklrAccess = {
        permission: 'standard',
        isCompanyWide: false,
        canPerformOnSiteControl: false,
        canSendSmsAlerts: false,
        cardEditPermission: 'none',
        cardsCoursesAndCertificatesPermission: 'none',
    } as BklrAccess

    permissions = [
        'standard',
        'administrator',
        'onlyReportsForCompany',
        'onlyPersonnelOnSite',
        'onlyCompaniesOnSite',
        'onlyPersonnelOnSiteAndHistory',
    ]

    cardsCoursesAndCertificatesPermission = [
        'none',
        'cardsForOwnCompany',
        'cardsForSubcontractors',
        'coursesAndCertificatesForOwnCompany',
        'coursesAndCertificatesForSubcontractors',
        'cardsCoursesAndCertificatesForOwnCompany',
        'cardsCoursesAndCertificatesForSubcontractor',
    ]

    get canConnectVisitorCard() {
        return (
            this.connectedVisitorCardId &&
            this.connectedVisitorCardId !== this.userInfo?.connectedVisitorCardId
        )
    }

    get hasBklrId() {
        return Boolean(this.currentProject.attributes.bklrId)
    }

    // If creating a project user for an existing user, the user must have an email address.
    get hasValidUserForBklr() {
        return this.hasBklrId
    }

    get userPhoneNumber() {
        return this.user['pendingDisplayName'] ? this.user['mobile'] : this.user.mobileNumber
    }

    get hseCardExists() {
        return Boolean(this.userInfo?.cardId) && this.userInfo?.cardId !== this.userPhoneNumber
    }

    get virtualCardExists() {
        return Boolean(this.userInfo?.cardId) && this.userInfo?.cardId === this.userPhoneNumber
    }

    get user(): ProjectUser {
        return this.props.user
    }

    get canEdit() {
        return !this.loading && this.user['canEdit']
    }

    get userName(): string {
        return this.user.userName ? this.user.userName : this.user.primaryEmail
    }

    get isPersonnelModuleDeleted() {
        return this.isModuleDeleted('personnel')
    }

    get personnelModuleActive() {
        return this.isModuleActive('personnel')
    }

    get canUpdateBklrAccess() {
        return this.bklrAccess.isCompanyWide === false
    }

    handleUseVirtualCardChange(value: boolean) {
        this.useVirtualCard = value
        if (value && !this.user['pendingDisplayName']) {
            if (this.user['name']) {
                const [firstName, ...names] = this.user['name'].split(' ')
                this.virtualCardUser.firstName = firstName || ''
                this.virtualCardUser.lastName = names?.join(' ') || ''
            }

            this.virtualCardUser.mobile = this.user.mobileNumber
        }
    }

    async confirmDeleteUser() {
        let confirm = await JsonApiObject.openDialog(UserDeletePromptDialog, { user: this.user })
        if (confirm) {
            this.$emit('close', 'user:deleted')
        }
    }

    updateContractor(contractors: any[]) {
        this.contractor = contractors[0] || null
        if (!this.contractor) {
            this.useVirtualCard = false
            this.hasBklrAccess = Boolean(this.userInfo?.bklrAccess) || false
        }
    }

    async getMoreUserInfo() {
        if (!this.hasBklrId) {
            return
        }

        try {
            const userResponse = await getRequest(
                this.getURL({
                    path: `/projectUsers/${this.user._id}?fields[projectUser]=cardId,connectedVisitorCardId,bklrAccess`,
                    base: process.env.VUE_APP_AUTHORIZATION_BASE_URL,
                })
            )
            this.userInfo = deserialise(userResponse.data).data
            if (this.userInfo?.connectedVisitorCardId) {
                this.connectedVisitorCardId = this.userInfo.connectedVisitorCardId
            }
            if (this.userInfo?.bklrAccess) {
                this.bklrAccess.permission = this.userInfo.bklrAccess.permission
                this.bklrAccess.isCompanyWide = this.userInfo.bklrAccess.isCompanyWide
                this.bklrAccess.canPerformOnSiteControl =
                    this.userInfo.bklrAccess.canPerformOnSiteControl || false
                this.bklrAccess.canSendSmsAlerts =
                    this.userInfo.bklrAccess.canSendSmsAlerts || false
                this.bklrAccess.cardEditPermission =
                    this.userInfo.bklrAccess.cardEditPermission || 'none'
                this.hasBklrAccess = true
            }
        } finally {
            this.loadingVirtualCard = false
        }
    }

    async createVirtualCardConnection(mobile: string, cardNumber: string | null) {
        const payload = serialise('visitorVirtualCardConnection', {
            mobile,
            visitorCardNumber: cardNumber,
            project: {
                data: {
                    type: 'project',
                    id: this.currentProjectId,
                },
            },
        })

        return postRequest(
            getURL({
                path: '/visitorVirtualCardConnections',
                base: process.env.VUE_APP_AUTHORIZATION_BASE_URL,
            }),
            {},
            payload.data
        )
    }

    async disconnectVisistorCard(mobile: string) {
        if (this.connectedVisitorCardId === null || this.removingVisitorCard) return

        try {
            this.removingVisitorCard = true
            await this.createVirtualCardConnection(mobile, null)
            this.connectedVisitorCardId = null
            if (this.userInfo) {
                this.userInfo.connectedVisitorCardId = null
            }
        } finally {
            this.removingVisitorCard = false
        }
    }

    async connectVisitorCard(mobile: string) {
        if (!this.connectedVisitorCardId) return

        try {
            this.connectingVisitorCard = true
            await this.createVirtualCardConnection(mobile, this.connectedVisitorCardId)
            if (this.userInfo) {
                this.userInfo.connectedVisitorCardId = this.connectedVisitorCardId
            }
        } catch (e) {
            const error = e?.data?.response?.data?.errors?.[0]
            if (error?.code === 'visitorCardNotFound') {
                this.$store.commit('setSuccessSnackBar', {
                    subtitle: this.$t(`fe_authorization['projectUser.virtualCardNotFound']`),
                    timeout: 5000,
                })
            }
        } finally {
            this.connectingVisitorCard = false
        }
    }

    async update() {
        if (this.loading || !this.canEdit) {
            return
        }

        this.loading = true

        if (this.useVirtualCard && this.virtualCardUserFormValid && this.contractor) {
            try {
                await postRequest(
                    getURL({
                        path: 'virtualCards',
                        base: process.env.VUE_APP_AUTHORIZATION_BASE_URL,
                    }),
                    {},
                    {
                        type: 'virtualCard',
                        attributes: {
                            ...this.virtualCardUser,
                            birthDate: this.virtualCardUser.birthDate
                                ? this.virtualCardUser.birthDate
                                : null,
                        },
                        relationships: {
                            company: {
                                data: {
                                    type: 'company',
                                    // @ts-ignore
                                    id:
                                        this.contractor.company._id ||
                                        this.contractor.company?.data?.id,
                                },
                            },
                        },
                    }
                )
            } catch (e) {
                const error = e?.data?.response?.data?.errors?.[0]
                if (error?.code === 'cardAlreadyExists') {
                    this.$store.commit('setSuccessSnackBar', {
                        subtitle: this.$t(`fe_authorization['projectUser.virtualCardExists']`, {
                            number: this.virtualCardUser.mobile,
                        }),
                        timeout: 5000,
                    })
                }
            }

            if (this.connectedVisitorCardId) {
                await this.connectVisitorCard(this.virtualCardUser.mobile)
            }
        }

        const data = {
            attributes: {
                jobTitle: this.jobTitle,
                jobDescription: this.jobDescription,
                ...(this.contractor &&
                    this.hasValidUserForBklr &&
                    this.canUpdateBklrAccess && {
                        bklrAccess: this.hasBklrAccess
                            ? {
                                  permission: this.bklrAccess.permission,
                                  ...(this.bklrAccess.permission == 'standard' && {
                                      canPerformOnSiteControl:
                                          this.bklrAccess.canPerformOnSiteControl,
                                      canSendSmsAlerts: this.bklrAccess.canSendSmsAlerts,
                                      cardEditPermission: this.bklrAccess.cardEditPermission,
                                  }),
                                  ...(this.bklrAccess.permission == 'onlyReportsForCompany' && {
                                      cardsCoursesAndCertificatesPermission:
                                          this.bklrAccess.cardsCoursesAndCertificatesPermission,
                                  }),
                              }
                            : null,
                    }),
            },
            relationships: {
                displayContractor: {
                    relData: this.contractor
                        ? {
                              type: 'contractor',
                              id: this.contractor._id || this.contractor.id,
                          }
                        : null,
                },
            },
        }

        try {
            await this.user.patchProjectUser(data).catch(async (error) => {
                this.error = true

                if (error.status == '412') {
                    this.$store.commit('closeSnackbar')
                    this.preconditionFailed = true
                }
            })
            if (!this.error) this.$emit('close', true)
        } finally {
            this.loading = false
        }
    }

    async created() {
        this.loadingVirtualCard = this.hasBklrId
        this.getMoreUserInfo()
    }
}
